/**
 * useQuery hook for the publication templates list (TEMPLATES tab).
 *
 * Replaces `publicationViewEffects.getTemplates` (compose-app). Mirrors the
 * same `getTemplates` Hasura document + variable builder via `postQuery`, then
 * shapes the response with `buildPaginationOutput`.
 *
 * Auth note (§1): non-admin reachable → `postQuery`, not `useGraphQLQuery`.
 * Pass `null` to disable during route transitions before `entityId > 0` (§9).
 */

import { useQuery } from '@tanstack/react-query';
import { DEFAULT_PER_PAGE } from '@opengov/stories-shared/constants';

import { postQuery } from '../../../../effects/publications/postQuery';
import { getTemplates } from '../../../../graphQL/queries';
import { buildPaginationOutput } from '../../../../transformations/listTransformation';
import { orderBy } from '../../../../utils';
import { useToast } from '../../../Toast/context/useToast.context';
import { IGetStoryGraphqlResponse, IStoryListGraphqlRequest, IStoryListing } from '../../../../../types';
import { publicationViewQueryKeys, TemplatesKeyParams } from './publicationViewQueryKeys';

export type UseTemplatesParams = TemplatesKeyParams | null;

export function useTemplates(params: UseTemplatesParams, errorMessage: string) {
  const { toggleToast } = useToast();

  return useQuery<IStoryListing, Error>({
    queryKey: params
      ? publicationViewQueryKeys.templates(params)
      : ([...publicationViewQueryKeys.all, 'templates', 'disabled'] as const),
    queryFn: async () => {
      if (!params) throw new Error('Disabled');
      const { isOpenGovAdmin, entityId, sort, ogId, perPage, page, shouldFilterByUser, title } = params;

      let variables: IStoryListGraphqlRequest = {
        limit: perPage || DEFAULT_PER_PAGE,
        offset: (page - 1) * (perPage || DEFAULT_PER_PAGE)
      };
      if (!isOpenGovAdmin && entityId) {
        variables = { ...variables, entityId };
      }
      if (sort) {
        variables = { ...variables, order: orderBy(sort) };
      }
      if (title) {
        variables = { ...variables, title: `%${title}%` };
      }
      if (shouldFilterByUser && ogId) {
        variables = { ...variables, opengovId: ogId };
      }

      const response = await postQuery(getTemplates, variables);
      const graphqlResponse: IGetStoryGraphqlResponse[] = response.resources;
      const count = response.count.aggregate.count;
      return buildPaginationOutput(page, count, graphqlResponse);
    },
    enabled: params !== null,
    onError: () => {
      toggleToast({ title: errorMessage, severity: 'error' });
    }
  });
}
